/**
 * Journal component (task 20.3, R7.1).
 *
 * Renders the FULL, unabridged chat log for player review — every entry, in
 * order, nothing dropped or summarized. Roll audits (R2.3), DM narration, and
 * chat all appear here as distinct entry kinds. The list is scrollable and
 * complete.
 */

import { memo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { JournalEntry } from "../journal.js";

const KIND_LABEL: Record<JournalEntry["kind"], string> = {
  chat: "Chat",
  dm: "Rufus",
  narration: "Narration",
  roll: "Roll",
  system: "System",
};

/**
 * The DM narrates in Markdown (bold, lists, the occasional heading), so DM-voiced
 * entries render it as formatted output instead of showing literal `**` / `-`
 * characters (the reported "markup in the chat output" bug). SECURITY: the
 * narration is UNTRUSTED model output, so this must never become an injection
 * sink. `react-markdown` builds a React element tree from the parsed syntax tree
 * — it does NOT use dangerouslySetInnerHTML — and WITHOUT `rehype-raw` any raw
 * HTML in the text (`<script>…`, `<img onerror=…>`) is treated as LITERAL TEXT,
 * not parsed into elements. We also:
 *   - restrict the allowed element set (no images — an `![](url)` would beacon /
 *     leak an IP; no raw headings dominating the chat), and
 *   - harden links: forced `rel="noopener noreferrer"` + `target="_blank"`, and
 *     the default react-markdown URL sanitizer already strips `javascript:` etc.
 */
const MARKDOWN_COMPONENTS: Components = {
  // Drop images entirely — render their alt text as plain words instead of an
  // element that fetches a URL (SSRF/beacon surface from untrusted narration).
  img: ({ alt }) => <>{alt ?? ""}</>,
  // Harden any link the model emits. (react-markdown already drops dangerous
  // URL schemes like `javascript:`; this adds the noopener/noreferrer + newtab.)
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

/** The entry kinds whose text is DM-authored prose and may carry Markdown. */
const MARKDOWN_KINDS: ReadonlySet<JournalEntry["kind"]> = new Set(["dm", "narration"]);

/**
 * Renders one DM-voiced entry's Markdown. MEMOIZED on the raw `text` (round-
 * markdown-review Low #2): the journal re-renders on every live push, and
 * re-parsing every DM entry's Markdown each time is wasted work on a long log.
 * `memo` keyed on the single `text` prop means an entry only re-parses when its
 * own text actually changes. (No ReDoS risk here — this is purely a perf win.)
 */
const DmMarkdown = memo(function DmMarkdown({ text }: { readonly text: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {text}
    </Markdown>
  );
});

export function Journal({
  entries,
  resolveAuthor,
}: {
  readonly entries: readonly JournalEntry[];
  /**
   * Map an entry's `author` (which, for chat, is a raw user id) to a display
   * name. Returns the readable name, or the input unchanged when unknown.
   * Defaults to identity so the component is usable without a resolver.
   */
  readonly resolveAuthor?: (author: string) => string;
}) {
  const nameFor = resolveAuthor ?? ((a: string) => a);
  return (
    <section aria-label="Journal" className="journal">
      <h2>Journal</h2>
      {/*
        Live region (WCAG 4.1.3): the journal is where the actual game
        narration, chat, and roll audit land, so screen-reader users must hear
        new entries — not just the "Rufus is writing…" status. `polite` so it
        does not interrupt; the id-based dedupe in appendJournalEntry means a
        reconnect replay of the same id replaces in place rather than
        re-announcing.
      */}
      <ol
        className="journal-entries"
        data-testid="journal-entries"
        aria-live="polite"
        aria-relevant="additions"
      >

        {entries.length === 0 ? (
          <li className="journal-empty">The journal is empty.</li>
        ) : (
          entries.map((entry) => (
            <li key={entry.id} className={`journal-entry kind-${entry.kind}`}>
              {/*
                The speaker prefix. We render the author's display name when
                present (e.g. "Rufus", a player's character name), else fall
                back to the kind label ("System"). The trailing ": " is a real
                text node so the label never visually smashes into the entry
                text (the reported bug), and it stays part of the readable line
                for screen readers.
              */}
              <span className="journal-speaker">
                {entry.author ? nameFor(entry.author) : KIND_LABEL[entry.kind]}
                {entry.mode === "ooc" ? " (out of character)" : ""}
                {": "}
              </span>
              <span className="journal-text">
                {MARKDOWN_KINDS.has(entry.kind) ? (
                  // DM-voiced prose → render Markdown (safe: no raw HTML, no
                  // dangerouslySetInnerHTML; see MARKDOWN_COMPONENTS note).
                  // Memoized per entry text to avoid re-parsing on every push.
                  <DmMarkdown text={entry.text} />
                ) : (
                  // Player chat / roll / system lines are literal text — never
                  // reinterpret a player's own message as Markdown.
                  entry.text
                )}
              </span>
            </li>
          ))
        )}
      </ol>
    </section>
  );
}
